import Http from '@/services/http';
import {
  ChartQuery,
  GetRegionalByCompaniesRequest,
  Lookup,
  AssetsRequestByInstallations,
  AssetResponse,
  OscillographyInstallationsPagedRequest,
  PagedResponseInstallation,
  OscillographyManeuversRequest,
  OscillographyManeuverResponse,
  OscillographyReportResponse,
  OscillographyVariablesRequest,
  OscillographyVariablesResponse,
  OscillographyReportListRequest,
  OscillographyReportListResponse,
  OscillographySaveReportRequest,
  OscillographyReportView,
  VariableTypes,
  OscillographyAlarmInfoData,
  InfoData,
} from 'sigma-mappings';
import translate from '@/services/translate';
import {
  getLineTypesListByLength,
  getLineTypesBookmarkListByLength,
  getLineWidthListByLength,
  getLineSymbolsListByLength,
  getLineSymbolColorListByLength,
} from '@/domain/data/charts/chartLineTypes';
import oscillographyChartTypes from '@/domain/enums/oscillographyChartTypes';

const chartFieldTypes = [
  VariableTypes.OSCILLOGRAPHY,
  VariableTypes.OSCILLOGRAPHY_REFERENCE,
];

const diagnosticFieldTypes = [
  VariableTypes.GRAPHIC_OSCILLOGRAPHY_DIAGNOSTIC,
];

const infoDataByVariableTypes = {
  [VariableTypes.OSCILLOGRAPHY_INFO_AUX]: 'additionalInfo',
  [VariableTypes.OSCILLOGRAPHY_ALARM]: 'alarms',
  [VariableTypes.OSCILLOGRAPHY_ALARM_CONFIG]: 'alarmsConfigs',
  [VariableTypes.OSCILLOGRAPHY_DIAGNOSTIC]: 'diagnostics',
  [VariableTypes.GRAPHIC_OSCILLOGRAPHY_DIAGNOSTIC]: 'diagnostics',
};

function chartNameFormatter(itemField, oscillographyReport, isDiagnostics = false) {
  const maneuver = oscillographyReport.isOscillographyReference
    ? translate('reports.oscillography.view-report.referenceManeuver')
    : translate('reports.oscillography.view-report.maneuver');
  const title = `${maneuver}: ${oscillographyReport.oscillographyId}`;
  const moduleIdentity = oscillographyReport.setGroup
    ? `${oscillographyReport.moduleName} - ${oscillographyReport.setGroup}`
    : '';
  let words = [];
  if (isDiagnostics) {
    words = [
      oscillographyReport.installationName,
      oscillographyReport.assetName,
      moduleIdentity,
      itemField.name,
    ];
  } else {
    words = [
      oscillographyReport.installationName,
      oscillographyReport.assetName,
      moduleIdentity,
      title,
      itemField.name,
    ];
  }
  return words.filter(Boolean).join(' - ');
}

function maneuverKeyFormatter(oscillographyReport) {
  return [
    oscillographyReport.oscillographyUniqueIdentifierIED,
    oscillographyReport.assetModuleId,
    oscillographyReport.setGroup,
  ].filter(Boolean).join(':');
}

function variableNameFormatter(itemField, oscillographyReport) {
  const reference = oscillographyReport.isOscillographyReference
    ? `${translate('reports.oscillography.view-report.reference')}: `
    : '';
  const words = [reference, itemField.name];
  return words.join('');
}

function maneuverNameFormatter(oscillographyReport) {
  const maneuver = oscillographyReport.isOscillographyReference
    ? translate('reports.oscillography.view-report.referenceManeuver')
    : translate('reports.oscillography.view-report.maneuver');
  return `${maneuver}: ${oscillographyReport.oscillographyId}`;
}

function mapChartData(oscillographyReport, fieldType) {
  const itemFieldTypes = oscillographyReport.fields.filter(field =>
    // eslint-disable-next-line implicit-arrow-linebreak
    fieldType.includes(field.fieldType));
  const result = itemFieldTypes.map(itemField => ({
    ...itemField,
    oscillographyId: oscillographyReport.oscillographyId,
    oscillographyUUID: oscillographyReport.oscillographyUniqueIdentifierIED,
    maneuverKey: maneuverKeyFormatter(oscillographyReport),
    name: chartNameFormatter(itemField, oscillographyReport),
    variableName: variableNameFormatter(itemField, oscillographyReport),
    dateTimeOscillography: oscillographyReport.dateTimeOscillography,
    axis: itemField.axis,
  }));
  return result;
}


function mapManeuvers(oscillographyReport) {
  const infoData = {
    additionalInfo: [],
    alarms: [],
    alarmsConfigs: [],
    diagnostics: [],
  };

  const assetInfo = [];

  assetInfo.push({
    key: 'dateTimeOscillography',
    value: oscillographyReport.dateTimeOscillography,
  });
  assetInfo.push({
    key: 'dateTimeDownload',
    value: oscillographyReport.dateTimeDownload,
  });
  assetInfo.push({
    key: 'oscillographySubtype',
    value: oscillographyReport.oscillographySubtype,
  });
  assetInfo.push({
    key: 'oscillographyId',
    value: oscillographyReport.oscillographyId,
  });
  assetInfo.push({
    key: 'referenceOscillographyId',
    value: oscillographyReport.referenceOscillographyId || '',
  });
  assetInfo.push({
    key: 'oscillographyUniqueIdentifierIED',
    value: oscillographyReport.oscillographyUniqueIdentifierIED,
  });
  assetInfo.push({
    key: 'referenceOscillographyUniqueIdentifierIED',
    value: oscillographyReport.referenceOscillographyUniqueIdentifierIED || '',
  });

  oscillographyReport.fields.forEach(maneuver => {
    let { fieldType } = maneuver;

    if (!fieldType && maneuver.tipoCampo) {
      if (maneuver.tipoCampo.includes('Grafico') && maneuver.tipoCampo.includes('OscilografiaDiagnostico')) {
        fieldType = VariableTypes.GRAPHIC_OSCILLOGRAPHY_DIAGNOSTIC;
      } else if (maneuver.tipoCampo.includes('OscilografiaDiagnostico')) {
        fieldType = VariableTypes.OSCILLOGRAPHY_DIAGNOSTIC;
      } else if (maneuver.tipoCampo.includes('OscilografiaInfoAuxiliar')) {
        fieldType = VariableTypes.OSCILLOGRAPHY_INFO_AUX;
      } else if (maneuver.tipoCampo.includes('OscilografiaAlarme')) {
        fieldType = VariableTypes.OSCILLOGRAPHY_ALARM;
      } else if (maneuver.tipoCampo.includes('OscilografiaAlarmeConfig')) {
        fieldType = VariableTypes.OSCILLOGRAPHY_ALARM_CONFIG;
      }
    }

    const field = infoDataByVariableTypes[fieldType];
    if (!field) return;
    const data = field === 'alarms'
        ? new OscillographyAlarmInfoData(maneuver)
        : new InfoData(maneuver);
    infoData[field].push(data);
  });
  return {
    infoData,
    assetInfo,
    moduleName: oscillographyReport.moduleName,
    maneuverId: oscillographyReport.oscillographyId,
    maneuverUUID: oscillographyReport.oscillographyUniqueIdentifierIED,
    maneuverKey: maneuverKeyFormatter(oscillographyReport),
    name: maneuverNameFormatter(oscillographyReport),
    assetName: oscillographyReport.assetName,
    dateTimeOscillography: oscillographyReport.dateTimeOscillography,
    installationName: oscillographyReport.installationName,
    companyName: oscillographyReport.companyName,
    isReference: oscillographyReport.isOscillographyReference,
  };
}

function mapReportView(oscillographyReportViewList) {
  let chartData = [];
  let diagnosticsChartData = [];
  let maneuvers = [];
  oscillographyReportViewList.forEach(oscillographyReport => {
    chartData = chartData.concat(mapChartData(oscillographyReport, chartFieldTypes));
    diagnosticsChartData = diagnosticsChartData.concat(
      mapChartData(oscillographyReport, diagnosticFieldTypes),
    );
    maneuvers = maneuvers.concat(mapManeuvers(oscillographyReport));
  });
  maneuvers.sort((a, b) => {
    if (a.isReference && !b.isReference) return -1;
    if (!a.isReference && b.isReference) return 1;
    return a.name.localeCompare(b.name);
  });

    const chartLineTypes = getLineTypesListByLength(maneuvers.length);
    const chartLineTypesBookmark = getLineTypesBookmarkListByLength(maneuvers.length);
    const chartLineWidth = getLineWidthListByLength(maneuvers.length);
    const chartLineSymbols = getLineSymbolsListByLength(maneuvers.length);
    const chartLineSymbolColor = getLineSymbolColorListByLength(maneuvers.length);

    const maneuverIndexMap = {};
    maneuvers.forEach(
      // eslint-disable-next-line no-return-assign, no-param-reassign
      (maneuver, index) => {
        // eslint-disable-next-line no-param-reassign
        maneuver.preview = {
          lineTypes: chartLineTypes[index],
          lineTypesBookmark: chartLineTypesBookmark[index],
          lineWidth: chartLineWidth[index],
          lineSymbols: chartLineSymbols[index],
          lineSymbolColor: chartLineSymbolColor[index],
        };
        maneuverIndexMap[maneuver.maneuverKey] = index;
      },
    );

  chartData.forEach(chartItem => {
    // eslint-disable-next-line no-param-reassign
    chartItem.maneuverIndex = maneuverIndexMap[chartItem.maneuverKey];
  });
  const groupedDiagnosticsChartData = Object.values(
    diagnosticsChartData.reduce((acc, item) => {
      const key = item.variableName;
      if (!acc[key]) {
        acc[key] = {
          name: item.name,
          unit: item.unit,
          variableName: item.variableName,
          oscillographyChartType: oscillographyChartTypes.DIAGNOSTICS,
          axis: item.axis,
          values: [],
        };
      }
      acc[key].values.push({
        x: item.dateTimeOscillography,
        y: item.values?.[0]?.y ?? 0,
        maneuver: item.maneuverKey,
        maneuverId: item.oscillographyId,
      });
      return acc;
    }, {}),
  );

  groupedDiagnosticsChartData.forEach(group => {
    group.values.sort((a, b) => new Date(a.x) - new Date(b.x));
  });

  diagnosticsChartData = groupedDiagnosticsChartData;
  return {
    chartData,
    diagnosticsChartData,
    maneuvers,
  };
}

const urls = {
  view: {
    remove: reportId => `/oscilografia/${reportId}`,
    getReportList: '/oscilografia/relatorio/listagem',
    reportView: reportId => `/oscilografia/${reportId}/visualizacao`,
    download: '/Oscilografia/listagem/exportar/excel',
  },
  config: {
    getCompanies: '/oscilografia/empresas',
    getRegional: '/oscilografia/regionais',
    getInstallations: '/oscilografia/instalacoes/listagem',
    getAssets: 'oscilografia/ativos',
    getManeuvers: '/oscilografia/manobras',
    getVariables: '/oscilografia/variaveis',
    getModules: '/oscilografia/modulos/lookup',
    getReport: reportId => `/oscilografia/${reportId}`,
    deleteOscillographyReport: reportId => `/oscilografia/${reportId}`,
    save: '/oscilografia',
    edit: id => `/oscilografia/${id}`,
  },
};

function getManeuversRequestFieldName(fieldName) {
  switch (fieldName) {
    case 'Ativos':
      return translate('fields.asset.select-assets');
    case 'TiposManobras':
      return translate('fields.asset.select-maneuvers-types');
    case 'SubTiposManobras':
      return translate('fields.asset.select-oscillographs-subtypes');
    default:
      return fieldName;
  }
}

function getMA001Params({ field }) {
  return {
    NomeDoCampo: `"${getManeuversRequestFieldName(field)}"`,
  };
}

/**
 * Dados da manobra. Utilizado na listagem de manobras, ao recuperar ou salvar uma oscilografia.
 * @typedef ManeuverData
 * @property {Number} oscillographyId
 * @property {Number} number
 * @property {String} dateAndTime
 * @property {String} maneuverType
 * @property {String} maneuverSubType
 * @property {bool} qualityAchieved
 * @property {String} assetId
 * @property {String} assetName
 * @property {String} installationName
 * @property {String} companyName
 * @property {String} moduleName
 * @property {bool} outOfReference
 */

export default {
  /** Aba Instalações - Novo relatório
   *  Traz o lookup de empresas para filtrar as instalações
   */
  getCompanies() {
    const http = new Http();
    return http
      .get(urls.config.getCompanies)
      .then(({ data }) => data.map(item => new Lookup(item)));
  },

  /** Aba Instalações - Novo relatório
   *  Traz o lookup de regionais para filtrar as instalações
   *  @param {companiesIds} payload.companiesIds - Filtra as regionais
   *    de acordo com o filtro de empresas.
   */
  getRegional(payload) {
    const http = new Http();
    const params = new GetRegionalByCompaniesRequest(payload);
    return http
      .get(urls.config.getRegional, { params })
      .then(({ data }) => data.map(item => new Lookup(item)));
  },

  /** Aba Ativos/Manobras - Novo relatório
   *  Traz o lookup de ativos para filtrar as manobras
   *  @param {assetTypeId} payload.assetTypeId
   *  @param {installationsIds} payload.installationsIds
   */
  getAssets(payload) {
    const http = new Http();
    const mappedPayload = new AssetsRequestByInstallations(payload);
    return http
      .post(urls.config.getAssets, mappedPayload)
      .then(({ data }) => data.map(item => new AssetResponse(item)));
  },

  /** Aba Instalações - Novo relatório
   *  Obtém a listagem principal da aba para posteriormente trazer
   *    os ativos da aba de ativos e manobras.
   *  @param {reportId} payload.reportId - Em caso de edição traz
   *    as instalações selecionadas no relatório.
   *  @param {companiesIds} payload.companiesIds - Filtro
   *  @param {regionalIds} payload.regionalIds - Filtra
   */
  getInstallations(payload) {
    const http = new Http();
    const mappedPayload = new OscillographyInstallationsPagedRequest(payload);
    return http
      .post(urls.config.getInstallations, mappedPayload)
      .then(({ data }) => new PagedResponseInstallation(data));
  },

  /**
   * Listar as manobras disponíveis para serem selecionadas
   * @param {Array} filters.maneuverTypes Tipos de manobras
   * @param {Array} filters.maneuverSubTypes SubTipos de manobras
   * @param {String} filters.initialDateAndTime Data inicial
   * @param {String} filters.finalDateAndTime Data final
   * @param {Array} filters.assets Array de ids dos ativos
   * @param {Array} filters.registeredOscillographs Array das oscilografia selecionadas, objeto
   * com assetId e oscillographyId.
   * @returns {ManeuverData[]} Array de dados das manobras. Ver definição de @see ManeuverData;
   */
  getManeuvers(filters) {
    const http = new Http({
      errorHandling: {
        responseMessageParams: {
          $MA001: getMA001Params,
        },
      },
    });
    const params = new OscillographyManeuversRequest(filters);
    return http
      .post(urls.config.getManeuvers, params)
      .then(({ data }) => new OscillographyManeuverResponse(data));
  },

  /**
   * Obtém os módulos de oscilografia
   * @param {String} ativoId Id do Ativo com módulos de oscilografia
   */
  getModules(ativosIds) {
    const http = new Http({
      errorHandling: {
        responseMessageParams: {
          $MA001: getMA001Params,
        },
      },
    });
    return http
      .post(urls.config.getModules, { AtivosIds: ativosIds })
      .then(({ data }) => data.map(item => new Lookup(item)));
  },

  /**
   * Obtém os dados de configuração do relatório por id. Útil para edição, por exemplo.
   * @param {String} reportId Id do relatório de oscilografia
   */
  getReport(reportId) {
    const http = new Http();
    return http
      .get(urls.config.getReport(reportId))
      .then(({ data }) => new OscillographyReportResponse(data));
  },

  /**
   * Lista as variáveis de oscilografia, com base no tipo de ativo e nos tipos de manobras,
   * retorna um array agrupado por tipo de variável
   * @param {String} filters.assetTypeId Id do tipo de Ativo,
   * @param {Array} filters.maneuverTypes Tipos de manobras
   */
  getVariables(filters) {
    const http = new Http();
    const params = new OscillographyVariablesRequest(filters);
    return http
      .get(urls.config.getVariables, { params })
      .then(({ data }) => new OscillographyVariablesResponse(data));
  },

  getReportList(payload) {
    const http = new Http();
    const mappedPayload = new OscillographyReportListRequest(payload);
    return http
      .post(urls.view.getReportList, mappedPayload)
      .then(({ data }) => new OscillographyReportListResponse(data));
  },

  getReportView(reportId, chartFilters) {
    const http = new Http();
    const chartQuery = new ChartQuery(chartFilters);
    return http
      .get(urls.view.reportView(reportId), { params: chartQuery })
      .then(({ data }) => {
        const mappedResponse = data.manobras.map(
          item => new OscillographyReportView(item),
        );
        return mapReportView(mappedResponse);
      });
  },

  /**
   * Cria ou altera um relatório de oscilografia.
   * O preenchimento do campo Id irá indicar o comportamento a ser seguido, sem Id indica inserção.
   * @param {String} [reportData.id] Id do relatório (obrigatório em caso de edição)
   * @param {String} [reportData.reportName] Nome do template. Não utilizado em relatório
   * temporário.
   * @param {String} reportData.assetTypeId
   * @param {bool} reportData.transient Verdadeiro para relatórios temporários
   * @param {Object} reportData.template Estrutura com os dados do template
   * @param {ManeuverData[]} reportData.template.maneuvers Coleção de dados das manobras
   * selecionadas.
   * @param {Array} reportData.template.variables Array de Ids das variáveis
   * @param {Array} reportData.template.referenceVariables Array de Ids de variáveis de referência
   */
  saveReport(reportData) {
    const http = new Http();
    const params = new OscillographySaveReportRequest(reportData);
    if (reportData.id && !reportData.transient) {
      return this.editReport(reportData);
    }
    return http
      .post(urls.config.save, params)
      .then(({ data }) => ({ reportId: data }));
  },
  editReport(reportData) {
    const http = new Http();
    const params = new OscillographySaveReportRequest(reportData);
    return http
      .put(urls.config.edit(reportData.id), params)
      .then(({ data }) => ({ reportId: data }));
  },
  /**
   * Remove um relatório de oscilografia
   * @param {String} reportId Id do relatório
   */
  removeReport(reportId) {
    const http = new Http();
    return http
      .delete(urls.view.remove(reportId))
      .then(({ status }) => status === 200);
  },
  /**
   * Faz o download de um relatório.
   * @param {GUID} id Id do relatório.
   */
  downloadReport(id) {
    const http = new Http({
      download: true,
    });

    return http.post(urls.view.download, { id });
  },
};
